/**
 * Badge — small inline pill, used for direction labels ("Up", "Down",
 * "Stable") and quick valence markers in tables and prose.
 *
 * Two ways to reach this component:
 *   1. The `<badge tone="…">label</badge>` HTML-like directive (see
 *      `remark-tone-badges.ts` and `tone-badge-markers.ts`) —
 *      caller-driven, lax parser (PLT-4590).
 *   2. The auto-badge transform in `remark-table-sign-color.ts` for the
 *      "Direction" column — content-driven (the *value* of the cell, not
 *      the column name, picks the tone).
 *
 * Inline-block, no padding-driven height shifts: the badge respects its
 * surrounding line height so a row of badges doesn't break tabular
 * alignment.
 */
import type { ReactNode } from 'react';
import { coerceTone, type Tone } from './tone';

export interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  /**
   * Optional aria-label override. Defaults to a sensible reading of
   * `children` for screen readers; pass an explicit string when the
   * children contain glyphs (arrows, etc.) that read poorly.
   */
  ariaLabel?: string;
}

export function Badge({ tone = 'neutral', children, ariaLabel }: BadgeProps) {
  const safeTone: Tone = coerceTone(tone);
  return (
    <span className={`tone-badge tone-badge--${safeTone}`} aria-label={ariaLabel}>
      {children}
    </span>
  );
}
